import { keyframes } from '@emotion/react';
import { Content, Header, Item, Root, Trigger } from '@radix-ui/react-accordion';
import { styled } from '@sds-system/system';
import { getAllStyledProps } from '@sds-system/utils';
import { processNestedCSS } from '@sds-system/utils/styledUtils/nestedCSS';
import type {
  AccordionSizeKey,
  StyledAccordionProps,
  StyledAccordionRootProps,
} from './Accordion.types';

const slideDown = keyframes`
  from {
    height: 0;
    opacity: 0;
  }
  to {
    height: var(--radix-accordion-content-height);
    opacity: 100%;
  }
`;
const slideUp = keyframes`
  from {
    height: var(--radix-accordion-content-height);
    opacity: 100%;
  }
  to {
    height: 0;
    opacity: 0;
  }
`;

export const accordionSizeVariant: Record<AccordionSizeKey, number> = {
  M: 40,
  L: 48,
  XL: 56,
};

export const AccordionRoot = styled(Root)<StyledAccordionRootProps>(({ theme }) => {
  return {
    backgroundColor: theme.palette.bg.primary,
  };
}, getAllStyledProps);

export const AccordionItem = styled(Item)(() => {
  return {};
});

export const AccordionHeader = styled(Header)(() => {
  return {
    display: 'flex',
  };
});

export const AccordionTrigger = styled(Trigger)<StyledAccordionProps>(
  ({ theme, size = 'M', activeColor, justifyContent, gap }) => {
    return {
      display: 'flex',
      width: '100%',
      justifyContent: justifyContent,
      gap: gap,
      padding: '16px',
      alignItems: 'center',
      height: accordionSizeVariant[size],
      '& .ArrowDown': {
        transition: 'transform 300ms',
      },
      "&[data-state='open']": processNestedCSS(
        {
          ...(activeColor && {
            backgroundColor: activeColor,
          }),
          '& .ArrowDown': {
            transform: 'rotate(180deg)',
          },
        },
        theme,
      ),
      "&[data-state='closed']": processNestedCSS(
        {
          ...(activeColor && {
            backgroundColor: theme.palette.bg.primary,
          }),
        },
        theme,
      ),
    };
  },
  getAllStyledProps,
);

export const AccordionContent = styled(Content)(() => {
  return {
    overflow: 'hidden',
    "&[data-state='open']": {
      animation: `${slideDown} 0.3s `,
    },
    "&[data-state='closed']": {
      animation: `${slideUp} 0.3s `,
      animationFillMode: 'forwards',
    },
  };
});
